import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/client";
import { useAuth } from "../contexts/AuthContext";
import { readPlanId, readPlanStatus } from "../services/plan";
import { adminActivateTrial, adminRevokeTrial, adminConvertToPro, adminConvertToPremium } from "../services/trialService";
import {
  AlertTriangle,
  Ban,
  CheckSquare,
  ChevronDown,
  Crown,
  Eye,
  EyeOff,
  Filter,
  FlaskConical,
  Loader2,
  Search,
  ShieldBan,
  Square,
  Trash2,
  UserCheck,
  UserX,
  X,
  Zap,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

type PlanId = "free" | "trial" | "pro" | "premium";

type ManagedUser = {
  uid: string;
  name: string;
  email: string;
  planTier: PlanId;
  planStatus: string;
  status: string;        // active | inactive | deleted
  isTest: boolean;
  isPremium: boolean;
  trialUsed: boolean;
  trialExpiresAt: Timestamp | null;
  createdAt: Timestamp | null;
};

type ModalKind =
  | { type: "soft_delete"; uids: string[] }
  | { type: "perm_delete"; uids: string[] }
  | { type: "change_plan"; uids: string[] }
  | { type: "clean_test"; uids: string[] }
  | null;

type BulkAction =
  | "soft_delete" | "perm_delete" | "cancel" | "activate"
  | "deactivate" | "mark_test" | "change_plan";

// ─── Constants ────────────────────────────────────────────────────────────────

const PLAN_OPTIONS: { value: PlanId; label: string }[] = [
  { value: "free", label: "Free" },
  { value: "trial", label: "Trial" },
  { value: "pro", label: "Pro" },
  { value: "premium", label: "Premium" },
];

const PLAN_BADGE: Record<PlanId, string> = {
  free: "bg-slate-700/60 text-slate-300 border-slate-600/40",
  trial: "bg-blue-500/15 text-blue-300 border-blue-500/25",
  pro: "bg-cyan-500/15 text-cyan-300 border-cyan-500/25",
  premium: "bg-yellow-500/15 text-yellow-300 border-yellow-500/25",
};

const PLAN_ICON: Record<PlanId, React.ReactNode> = {
  free: <Zap className="w-3 h-3" />,
  trial: <FlaskConical className="w-3 h-3" />,
  pro: <FlaskConical className="w-3 h-3" />,
  premium: <Crown className="w-3 h-3" />,
};

const TEST_EMAIL_DOMAINS = ["gmail.com", "hotmail.com", "yahoo.com", "outlook.com"];

const ADMIN_EMAIL = "jessediasmunizjunior@gmail.com";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTs(ts: Timestamp | null): string {
  if (!ts) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }).format(ts.toDate());
  } catch { return "—"; }
}

function isTestEmail(email: string): boolean {
  const lower = email.toLowerCase();
  return TEST_EMAIL_DOMAINS.some((d) => lower.endsWith(`@${d}`));
}

// ─── Audit logger ─────────────────────────────────────────────────────────────

async function auditLog(params: {
  admin: string;
  action: string;
  targetUids: string[];
  targetEmails?: string[];
  details?: Record<string, unknown>;
}) {
  try {
    await addDoc(collection(db, "adminAuditLogs"), {
      admin: params.admin,
      action: params.action,
      targetUid: params.targetUids.length === 1 ? params.targetUids[0] : params.targetUids,
      targetEmail: params.targetEmails?.length === 1 ? params.targetEmails[0] : params.targetEmails,
      details: params.details ?? null,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("[AdminAudit][log:error]", e);
  }
}

// ─── Confirmation Modal ────────────────────────────────────────────────────────

const ConfirmModal: React.FC<{
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  confirmCls?: string;
  requireTyping?: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ title, description, confirmLabel, confirmCls, requireTyping, loading, onConfirm, onCancel }) => {
  const [typed, setTyped] = useState("");

  const canConfirm = !requireTyping || typed === requireTyping;

  return (
    <div className="fixed inset-0 z-[200] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-3xl border border-slate-800/80 bg-slate-950 shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-slate-800/60">
          <h2 className="text-lg font-black text-white">{title}</h2>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="text-sm text-slate-300 leading-relaxed">{description}</div>
          {requireTyping && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                Digite <span className="text-red-400 font-black">{requireTyping}</span> para confirmar
              </p>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={requireTyping}
                className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500/50 placeholder:text-slate-600 font-mono"
                autoFocus
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800/60">
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 px-4 py-2 text-sm font-bold text-slate-200 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm || loading}
            className={`inline-flex items-center gap-2 rounded-xl px-5 py-2 text-sm font-black text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${confirmCls ?? "bg-red-600 hover:bg-red-500"}`}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Plan Selector Modal ───────────────────────────────────────────────────────

const PlanSelectorModal: React.FC<{
  count: number;
  loading: boolean;
  onConfirm: (plan: PlanId) => void;
  onCancel: () => void;
}> = ({ count, loading, onConfirm, onCancel }) => {
  const [plan, setPlan] = useState<PlanId>("pro");
  return (
    <div className="fixed inset-0 z-[200] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-3xl border border-slate-800/80 bg-slate-950 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-800/60">
          <h2 className="text-lg font-black text-white">Alterar plano</h2>
          <button onClick={onCancel}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="px-6 py-5 space-y-3">
          <p className="text-sm text-slate-400">{count} usuário{count !== 1 ? "s" : ""} selecionado{count !== 1 ? "s" : ""}</p>
          <div className="grid grid-cols-3 gap-2">
            {PLAN_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setPlan(o.value)}
                className={`flex flex-col items-center gap-1 rounded-xl border py-3 text-sm font-bold transition-all ${
                  plan === o.value ? "border-cyan-500/40 bg-cyan-500/15 text-cyan-300" : "border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-600"
                }`}
              >
                <span className={`inline-flex ${PLAN_BADGE[o.value].split(" ")[1]}`}>{PLAN_ICON[o.value]}</span>
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800/60">
          <button onClick={onCancel} className="rounded-xl border border-slate-700 bg-slate-800/60 px-4 py-2 text-sm font-bold text-slate-200">Cancelar</button>
          <button
            onClick={() => onConfirm(plan)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-600/90 hover:bg-cyan-600 px-5 py-2 text-sm font-black text-white disabled:opacity-50"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminUserManagement: React.FC = () => {
  const { user } = useAuth();
  const adminEmail = user?.email ?? ADMIN_EMAIL;

  const [allUsers, setAllUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyTest, setOnlyTest] = useState(false);
  const [onlyTrial, setOnlyTrial] = useState(false);
  const [planFilter, setPlanFilter] = useState<PlanId | "all">("all");
  const [modal, setModal] = useState<ModalKind>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const headerCheckRef = useRef<HTMLInputElement>(null);

  const deleteUserFn = httpsCallable(functions, "deleteUserAdmin");

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      setAllUsers(snap.docs.map((d) => {
        const raw = d.data();
        return {
          uid: d.id,
          name: raw.profile?.name || raw.name || "Sem nome",
          email: raw.profile?.email || raw.email || "",
          planTier: readPlanId(raw) as PlanId,
          planStatus: readPlanStatus(raw),
          status: raw.status || "active",
          isTest: raw.isTest === true,
          isPremium: raw.isPremium === true,
          trialUsed: raw.trialUsed === true,
          trialExpiresAt: raw.trialExpiresAt ?? null,
          createdAt: raw.createdAt ?? null,
        };
      }));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [user]);

  // ─ Filtered list ─
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return allUsers.filter((u) => {
      if (!showDeleted && u.status === "deleted") return false;
      if (onlyActive && u.status !== "active") return false;
      if (onlyTest && !u.isTest && !isTestEmail(u.email)) return false;
      if (onlyTrial && u.planTier !== "trial") return false;
      if (planFilter !== "all" && u.planTier !== planFilter) return false;
      if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allUsers, showDeleted, onlyActive, onlyTest, onlyTrial, planFilter, search]);

  // ─ Test users (for clean button) ─
  const testUsers = useMemo(() =>
    allUsers.filter((u) => u.status !== "deleted" && (u.isTest || isTestEmail(u.email))),
    [allUsers]
  );

  // ─ Selection helpers ─
  function toggleOne(uid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((u) => u.uid)));
    }
  }

  function selectAllFiltered() {
    setSelected(new Set(filtered.map((u) => u.uid)));
  }

  // Indeterminate header checkbox
  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate =
        selected.size > 0 && selected.size < filtered.length;
    }
  }, [selected.size, filtered.length]);

  const selectedUsers = useMemo(() =>
    allUsers.filter((u) => selected.has(u.uid)),
    [allUsers, selected]
  );

  function flash(msg: string) {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(""), 3500);
  }

  // ─ Batch update helper ─
  async function batchUpdate(
    uids: string[],
    data: Record<string, unknown>,
    action: string,
    emails?: string[]
  ) {
    setActionLoading(true);
    try {
      const batch = writeBatch(db);
      for (const uid of uids) {
        batch.update(doc(db, "users", uid), data);
      }
      await batch.commit();
      await auditLog({ admin: adminEmail, action, targetUids: uids, targetEmails: emails });
      setSelected(new Set());
      flash(`${uids.length} usuário${uids.length > 1 ? "s" : ""} atualizado${uids.length > 1 ? "s" : ""}.`);
    } catch (e) {
      console.error("[UserMgmt][batchUpdate]", e);
      flash("Erro ao executar ação.");
    } finally {
      setActionLoading(false);
      setModal(null);
    }
  }

  // ─ Soft delete ─
  async function execSoftDelete(uids: string[]) {
    await batchUpdate(
      uids,
      {
        status: "deleted",
        deletedAt: serverTimestamp(),
        deletedBy: adminEmail,
        isPremium: false,
        plan: null,
        subscriptionStatus: "canceled",
        aiEstimatedCostMonthly: 0,
      },
      "soft_delete",
      uids.map((uid) => allUsers.find((u) => u.uid === uid)?.email ?? "")
    );
  }

  // ─ Permanent delete ─
  async function execPermDelete(uids: string[]) {
    setActionLoading(true);
    const errors: string[] = [];
    try {
      for (const uid of uids) {
        try {
          await deleteUserFn({ uid, permanent: true });
        } catch (e: any) {
          console.error(`[UserMgmt][permDelete] uid=${uid}`, e);
          errors.push(uid);
        }
      }
      await auditLog({
        admin: adminEmail,
        action: "permanent_delete",
        targetUids: uids,
        details: { errors },
      });
      setSelected(new Set());
      flash(errors.length > 0
        ? `Concluído com ${errors.length} erro(s). Verifique o console.`
        : `${uids.length} usuário${uids.length > 1 ? "s" : ""} excluído${uids.length > 1 ? "s" : ""} permanentemente.`
      );
    } finally {
      setActionLoading(false);
      setModal(null);
    }
  }

  // ─ Clean test users ─
  async function execCleanTest(uids: string[]) {
    await batchUpdate(
      uids,
      {
        status: "deleted",
        deletedAt: serverTimestamp(),
        deletedBy: adminEmail,
        isPremium: false,
        plan: null,
        subscriptionStatus: "canceled",
      },
      "clean_test_users"
    );
  }

  // ─ Bulk action dispatch ─
  async function dispatchBulkAction(action: BulkAction, uids: string[], extra?: { plan?: PlanId }) {
    const emails = uids.map((uid) => allUsers.find((u) => u.uid === uid)?.email ?? "");
    switch (action) {
      case "cancel":
        await batchUpdate(uids, { subscriptionStatus: "canceled", isPremium: false, plan: null }, "cancel_subscription", emails);
        break;
      case "activate":
        await batchUpdate(uids, { status: "active", subscriptionStatus: "active" }, "activate", emails);
        break;
      case "deactivate":
        await batchUpdate(uids, { status: "inactive", subscriptionStatus: "inactive" }, "deactivate", emails);
        break;
      case "mark_test":
        await batchUpdate(uids, { isTest: true }, "mark_test", emails);
        break;
      case "change_plan":
        if (extra?.plan) {
          await batchUpdate(uids, {
            // escreve o objeto plan completo para sobrescrever qualquer assinatura anterior
            plan: {
              tier: extra.plan,
              id: extra.plan,
              status: extra.plan === "free" ? "free" : "active",
              currentPeriodEnd: null,
            },
            planTier: extra.plan,
            tier: extra.plan,
            isPremium: extra.plan === "premium",
            subscriptionStatus: extra.plan === "free" ? "free" : "active",
          }, "change_plan", emails);
        }
        break;
    }
  }

  const btnCls = "inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" />
        Carregando usuários...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ─ Toolbar ─ */}
      <div className="rounded-2xl border border-slate-800/70 bg-slate-900/60 px-5 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-40">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar nome ou email..."
              className="w-full bg-slate-800/70 border border-slate-700/60 text-slate-100 text-xs rounded-xl pl-8 pr-3 py-1.5 focus:outline-none focus:border-cyan-500/50 placeholder:text-slate-600"
            />
          </div>

          {/* Filter chips */}
          <button
            onClick={() => setShowDeleted((v) => !v)}
            className={`${btnCls} ${showDeleted ? "border-slate-500/40 bg-slate-700/60 text-slate-200" : "border-slate-700/50 bg-transparent text-slate-500 hover:text-slate-300"}`}
          >
            {showDeleted ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            Excluídos
          </button>
          <button
            onClick={() => { setOnlyActive((v) => !v); setOnlyTest(false); }}
            className={`${btnCls} ${onlyActive ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-700/50 bg-transparent text-slate-500 hover:text-slate-300"}`}
          >
            <UserCheck className="w-3.5 h-3.5" />
            Somente ativos
          </button>
          <button
            onClick={() => { setOnlyTest((v) => !v); setOnlyActive(false); }}
            className={`${btnCls} ${onlyTest ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-slate-700/50 bg-transparent text-slate-500 hover:text-slate-300"}`}
          >
            <FlaskConical className="w-3.5 h-3.5" />
            Somente testes
          </button>
          <button
            onClick={() => { setOnlyTrial((v) => !v); setOnlyActive(false); }}
            className={`${btnCls} ${onlyTrial ? "border-blue-500/30 bg-blue-500/10 text-blue-300" : "border-slate-700/50 bg-transparent text-slate-500 hover:text-slate-300"}`}
          >
            <FlaskConical className="w-3.5 h-3.5" />
            Somente trial
          </button>

          {/* Plan filter */}
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value as PlanId | "all")}
            className="bg-slate-800/70 border border-slate-700/60 text-slate-300 text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-cyan-500/50"
          >
            <option value="all">Todos os planos</option>
            <option value="free">Free</option>
            <option value="trial">Trial</option>
            <option value="pro">Pro</option>
            <option value="premium">Premium</option>
          </select>

          {/* Clean test users */}
          <button
            onClick={() => setModal({ type: "clean_test", uids: testUsers.map((u) => u.uid) })}
            disabled={testUsers.length === 0}
            className={`${btnCls} border-orange-500/25 bg-orange-500/8 text-orange-300 hover:bg-orange-500/15`}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Limpar testes ({testUsers.length})
          </button>
        </div>

        {/* Info row */}
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          <span className="text-slate-500">
            <Filter className="w-3 h-3 inline mr-1" />
            {filtered.length} de {allUsers.length} usuários
          </span>
          {selected.size > 0 && (
            <button
              onClick={() => setSelected(new Set())}
              className="text-slate-400 hover:text-white underline underline-offset-2 transition-colors"
            >
              Limpar seleção ({selected.size})
            </button>
          )}
          <button
            onClick={selectAllFiltered}
            className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
          >
            Selecionar todos desta busca ({filtered.length})
          </button>
        </div>
      </div>

      {/* ─ Bulk actions bar ─ */}
      {selected.size > 0 && (
        <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/6 px-5 py-3 flex flex-wrap items-center gap-2">
          <span className="text-sm font-black text-cyan-300 mr-1">
            {selected.size} selecionado{selected.size > 1 ? "s" : ""}
          </span>
          <button
            onClick={() => dispatchBulkAction("cancel", [...selected])}
            disabled={actionLoading}
            className={`${btnCls} border-amber-500/25 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15`}
          >
            <Ban className="w-3.5 h-3.5" />Cancelar assinatura
          </button>
          <button
            onClick={() => dispatchBulkAction("activate", [...selected])}
            disabled={actionLoading}
            className={`${btnCls} border-emerald-500/25 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15`}
          >
            <UserCheck className="w-3.5 h-3.5" />Ativar
          </button>
          <button
            onClick={() => dispatchBulkAction("deactivate", [...selected])}
            disabled={actionLoading}
            className={`${btnCls} border-slate-600/40 bg-slate-700/30 text-slate-300 hover:bg-slate-700/50`}
          >
            <UserX className="w-3.5 h-3.5" />Desativar
          </button>
          <button
            onClick={() => setModal({ type: "change_plan", uids: [...selected] })}
            disabled={actionLoading}
            className={`${btnCls} border-cyan-500/25 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/15`}
          >
            <Crown className="w-3.5 h-3.5" />Alterar plano
          </button>
          <button
            onClick={() => dispatchBulkAction("mark_test", [...selected])}
            disabled={actionLoading}
            className={`${btnCls} border-orange-500/25 bg-orange-500/8 text-orange-300 hover:bg-orange-500/15`}
          >
            <FlaskConical className="w-3.5 h-3.5" />Marcar como teste
          </button>
          <button
            onClick={() => setModal({ type: "soft_delete", uids: [...selected] })}
            disabled={actionLoading}
            className={`${btnCls} border-red-500/25 bg-red-500/8 text-red-300 hover:bg-red-500/15`}
          >
            <ShieldBan className="w-3.5 h-3.5" />Excluir (soft)
          </button>
          <button
            onClick={() => setModal({ type: "perm_delete", uids: [...selected] })}
            disabled={actionLoading}
            className={`${btnCls} border-red-700/40 bg-red-700/15 text-red-400 hover:bg-red-700/25`}
          >
            <Trash2 className="w-3.5 h-3.5" />Excluir definitivo
          </button>
          {actionLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
      )}

      {actionMsg && (
        <p className={`text-sm font-bold px-1 ${actionMsg.includes("Erro") ? "text-red-400" : "text-emerald-400"}`}>
          {actionMsg}
        </p>
      )}

      {/* ─ User table ─ */}
      <div className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-800/60">
                <th className="px-4 py-3 w-10">
                  <input
                    ref={headerCheckRef}
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="w-3.5 h-3.5 rounded accent-cyan-500 cursor-pointer"
                  />
                </th>
                {["Nome / Email", "Plano", "Status", "Cadastro", "Ações"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40">
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Nenhum usuário encontrado.</td></tr>
              )}
              {filtered.map((u) => {
                const isSelected = selected.has(u.uid);
                const isDeleted = u.status === "deleted";
                return (
                  <tr
                    key={u.uid}
                    onClick={() => toggleOne(u.uid)}
                    className={`cursor-pointer transition-colors ${isSelected ? "bg-cyan-500/8" : "hover:bg-slate-800/30"} ${isDeleted ? "opacity-40" : ""}`}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(u.uid)}
                        className="w-3.5 h-3.5 rounded accent-cyan-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 max-w-44">
                      <div className="flex items-center gap-1.5">
                        <p className={`font-semibold truncate ${isDeleted ? "line-through text-slate-500" : "text-slate-200"}`}>
                          {u.name}
                        </p>
                        {u.isTest && (
                          <span className="shrink-0 text-[9px] font-black rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-300 px-1.5 py-0.5">TEST</span>
                        )}
                      </div>
                      <p className="text-slate-500 truncate">{u.email}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-black uppercase ${PLAN_BADGE[u.planTier]}`}>
                        {PLAN_ICON[u.planTier]}
                        {u.planTier.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isDeleted ? (
                        <span className="text-red-400 font-bold text-[11px]">Excluído</span>
                      ) : u.planStatus === "active" ? (
                        <span className="text-emerald-400 font-semibold text-[11px]">Ativo</span>
                      ) : (
                        <span className="text-slate-500 text-[11px]">{u.planStatus}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatTs(u.createdAt)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {!isDeleted && (
                          <button
                            onClick={() => setModal({ type: "soft_delete", uids: [u.uid] })}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Excluir (soft)"
                          >
                            <ShieldBan className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => setModal({ type: "perm_delete", uids: [u.uid] })}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-600/10 transition-colors"
                          title="Excluir definitivamente"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setModal({ type: "change_plan", uids: [u.uid] })}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                          title="Alterar plano"
                        >
                          <Crown className="w-3.5 h-3.5" />
                        </button>
                        {!isDeleted && u.planTier !== "trial" && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Ativar trial para ${u.email}?`)) return;
                              setActionLoading(true);
                              try {
                                await adminActivateTrial(u.uid);
                                await auditLog({ admin: adminEmail, action: "trial_activate", targetUids: [u.uid], targetEmails: [u.email] });
                                flash(`Trial ativado para ${u.email}`);
                              } catch (e: any) {
                                flash(`Erro: ${e?.message}`);
                              } finally { setActionLoading(false); }
                            }}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                            title="Ativar trial"
                          >
                            <FlaskConical className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!isDeleted && u.planTier === "trial" && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Encerrar trial de ${u.email}?`)) return;
                              setActionLoading(true);
                              try {
                                await adminRevokeTrial(u.uid);
                                await auditLog({ admin: adminEmail, action: "trial_revoke", targetUids: [u.uid], targetEmails: [u.email] });
                                flash(`Trial encerrado para ${u.email}`);
                              } catch (e: any) {
                                flash(`Erro: ${e?.message}`);
                              } finally { setActionLoading(false); }
                            }}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-orange-400 hover:bg-orange-500/10 transition-colors"
                            title="Encerrar trial"
                          >
                            <Ban className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!isDeleted && u.planTier === "trial" && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Converter ${u.email} para PRO?`)) return;
                              setActionLoading(true);
                              try {
                                await adminConvertToPro(u.uid);
                                await auditLog({ admin: adminEmail, action: "trial_convert_pro", targetUids: [u.uid], targetEmails: [u.email] });
                                flash(`${u.email} convertido para PRO`);
                              } catch (e: any) {
                                flash(`Erro: ${e?.message}`);
                              } finally { setActionLoading(false); }
                            }}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                            title="Converter para Pro"
                          >
                            <UserCheck className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─ Modals ─ */}
      {modal?.type === "soft_delete" && (
        <ConfirmModal
          title="Excluir usuários (soft delete)"
          description={
            <div className="space-y-2">
              <p>{modal.uids.length} usuário{modal.uids.length > 1 ? "s" : ""} será{modal.uids.length > 1 ? "ão" : ""} marcado{modal.uids.length > 1 ? "s" : ""} como excluído{modal.uids.length > 1 ? "s" : ""}.</p>
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/8 px-3 py-2 text-amber-300 text-xs">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                O documento permanece no Firestore. Plano ativo, acesso premium e custos de IA serão removidos.
              </div>
              <div className="max-h-24 overflow-y-auto space-y-0.5">
                {modal.uids.map((uid) => {
                  const u = allUsers.find((x) => x.uid === uid);
                  return u ? <p key={uid} className="text-xs text-slate-400">{u.email || u.name}</p> : null;
                })}
              </div>
            </div>
          }
          confirmLabel="Excluir (soft)"
          confirmCls="bg-red-600/90 hover:bg-red-600"
          loading={actionLoading}
          onConfirm={() => execSoftDelete(modal.uids)}
          onCancel={() => setModal(null)}
        />
      )}

      {modal?.type === "perm_delete" && (
        <ConfirmModal
          title="Excluir definitivamente"
          requireTyping="EXCLUIR"
          description={
            <div className="space-y-2">
              <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2 text-red-300 text-xs">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                Ação irreversível. Remove Auth, Firestore (users, supportTickets, subscriptions) e dados de uso.
              </div>
              <div className="max-h-24 overflow-y-auto space-y-0.5">
                {modal.uids.map((uid) => {
                  const u = allUsers.find((x) => x.uid === uid);
                  return u ? <p key={uid} className="text-xs text-slate-400">{u.email || u.name}</p> : null;
                })}
              </div>
            </div>
          }
          confirmLabel="Excluir definitivamente"
          confirmCls="bg-red-700 hover:bg-red-600"
          loading={actionLoading}
          onConfirm={() => execPermDelete(modal.uids)}
          onCancel={() => setModal(null)}
        />
      )}

      {modal?.type === "change_plan" && (
        <PlanSelectorModal
          count={modal.uids.length}
          loading={actionLoading}
          onConfirm={(plan) => dispatchBulkAction("change_plan", modal.uids, { plan })}
          onCancel={() => setModal(null)}
        />
      )}

      {modal?.type === "clean_test" && (
        <ConfirmModal
          title="Limpar usuários de teste"
          description={
            <div className="space-y-2">
              <p>Os seguintes <strong className="text-white">{modal.uids.length} usuários</strong> serão removidos (soft delete):</p>
              <p className="text-xs text-slate-500">Critério: email com domínio gmail.com / hotmail.com / yahoo.com / outlook.com, ou marcados como isTest=true</p>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-800 bg-slate-900/50 p-3 space-y-1">
                {modal.uids.map((uid) => {
                  const u = allUsers.find((x) => x.uid === uid);
                  return u ? (
                    <div key={uid} className="flex items-center gap-2 text-xs">
                      <span className="text-slate-400">{u.email}</span>
                      {u.isTest && <span className="text-orange-400 font-bold text-[9px]">isTest</span>}
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          }
          confirmLabel="Limpar todos"
          confirmCls="bg-orange-600/90 hover:bg-orange-600"
          loading={actionLoading}
          onConfirm={() => execCleanTest(modal.uids)}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default AdminUserManagement;
